/**
 * Role-based permissions utility
 *
 * Permissions are ADDITIVE: if any of the user's roles grants a permission,
 * the user has that permission.
 *
 * Role definitions:
 *   admin          – full access to everything
 *   reporter       – TIRP only; read/edit but cannot add/delete sites or objects,
 *                    cannot manage DDL collections/items, cannot save DDL register
 *   operator       – same as reporter
 *   account_manager– same rules as reporter
 *   assessor       – same as reporter
 *   tirp_organizer – manages TIRP; can add/delete sites & objects; full DDL access;
 *                    access to TerraFlow Manager
 */

const ROLE_PERMISSIONS = {
  admin: {
    canAccessTerraFlow:  true,
    canAccessAdmin:      true,
    canAddTestSite:      true,
    canDeleteTestSite:   true,
    canAddObject:        true,
    canDeleteObject:     true,
    canManageDDL:        true,
    canSaveDDLRegister:  true,
    canManageInspections: true,
  },
  reporter: {
    canManageInspections: true,
  },
  operator: {
    canManageInspections: true,
  },
  account_manager: {
    canManageInspections: true,
  },
  assessor: {
    canManageInspections: true,
  },
  tirp_organizer: {
    canAccessTerraFlow:  true,
    canAddTestSite:      true,
    canDeleteTestSite:   true,
    canAddObject:        true,
    canDeleteObject:     true,
    canManageDDL:        true,
    canSaveDDLRegister:  true,
    canManageInspections: true,
  },
};

/**
 * Returns a flat permissions object for a given array of role strings.
 * All permissions default to false; each role additively enables its own set.
 */
export function getPermissions(roles = []) {
  const perms = {
    canAccessTerraFlow:  false,
    canAccessAdmin:      false,
    canAddTestSite:      false,
    canDeleteTestSite:   false,
    canAddObject:        false,
    canDeleteObject:     false,
    canManageDDL:        false,
    canSaveDDLRegister:  false,
    canManageInspections: false,
  };

  for (const role of roles) {
    const rp = ROLE_PERMISSIONS[role];
    if (!rp) continue;
    for (const [key, val] of Object.entries(rp)) {
      if (val) perms[key] = true;
    }
  }

  return perms;
}

/** Convenience: format a role key into a display label. */
export function formatRoleLabel(role) {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Human-readable can/cannot lists per role (used by Roles Info modal). */
export const ROLE_INFO = {
  admin: {
    can: [
      'Full access to all features',
      'Access to Admin Management',
      'Access to TerraFlow Manager',
      'Add and delete test sites and objects',
      'Manage DDL collections and items',
      'Save DDL Register configurations',
      'Add, edit and delete inspections',
    ],
    cannot: [],
  },
  reporter: {
    can: [
      'Access to TIRP (Reporting Portal)',
      'Edit test sites and objects',
      'Add, edit and delete inspections',
      'Interact with DDL Register (read-only, cannot save)',
    ],
    cannot: [
      'Access to TerraFlow Manager',
      'Access to Admin Management',
      'Add or delete test sites',
      'Add or delete objects',
      'Manage DDL collections or items',
      'Save DDL Register configurations',
    ],
  },
  operator: {
    can: [
      'Access to TIRP (Reporting Portal)',
      'Edit test sites and objects',
      'Add, edit and delete inspections',
      'Interact with DDL Register (read-only, cannot save)',
    ],
    cannot: [
      'Access to TerraFlow Manager',
      'Access to Admin Management',
      'Add or delete test sites',
      'Add or delete objects',
      'Manage DDL collections or items',
      'Save DDL Register configurations',
    ],
  },
  account_manager: {
    can: [
      'Access to TIRP (Reporting Portal)',
      'Edit test sites and objects',
      'Add, edit and delete inspections',
      'Interact with DDL Register (read-only, cannot save)',
    ],
    cannot: [
      'Access to TerraFlow Manager',
      'Access to Admin Management',
      'Add or delete test sites',
      'Add or delete objects',
      'Manage DDL collections or items',
      'Save DDL Register configurations',
    ],
  },
  assessor: {
    can: [
      'Access to TIRP (Reporting Portal)',
      'Edit test sites and objects',
      'Add, edit and delete inspections',
      'Interact with DDL Register (read-only, cannot save)',
    ],
    cannot: [
      'Access to TerraFlow Manager',
      'Access to Admin Management',
      'Add or delete test sites',
      'Add or delete objects',
      'Manage DDL collections or items',
      'Save DDL Register configurations',
    ],
  },
  tirp_organizer: {
    can: [
      'Access to TIRP (Reporting Portal)',
      'Access to TerraFlow Manager',
      'Add and delete test sites and objects',
      'Edit test sites and objects',
      'Add, edit and delete inspections',
      'Full access to DDL Collections and items',
      'Save DDL Register configurations',
    ],
    cannot: [
      'Access to Admin Management',
    ],
  },
};
